import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SemanticPageDocument from '@/components/semantic/SemanticPageDocument';
import { supabase } from '@/integrations/supabase/client';
import { getDocsAppPage } from '@/lib/semantic-content/pages';
import NotFound from '@/pages/NotFound';

type PublishedAppScreenshot = {
  id?: string;
  label?: string;
  imageUrl: string;
  isPrimary?: boolean;
};

type PublishedDocsAppEntry = {
  image_url: string | null;
  screenshots: unknown;
};

function normalizeScreenshots(entry: PublishedDocsAppEntry | null) {
  const screenshots = Array.isArray(entry?.screenshots)
    ? entry.screenshots
        .map<PublishedAppScreenshot | null>((item) => {
          if (!item || typeof item !== 'object') {
            return null;
          }

          const screenshot = item as Record<string, unknown>;
          const imageUrl = typeof screenshot.imageUrl === 'string' ? screenshot.imageUrl.trim() : '';

          if (!imageUrl) {
            return null;
          }

          return {
            id: typeof screenshot.id === 'string' ? screenshot.id : undefined,
            label: typeof screenshot.label === 'string' ? screenshot.label : undefined,
            imageUrl,
            isPrimary: screenshot.isPrimary === true,
          };
        })
        .filter((item): item is PublishedAppScreenshot => Boolean(item))
    : [];

  const sortedScreenshots = screenshots.sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));

  if (sortedScreenshots.length > 0) {
    return sortedScreenshots;
  }

  if (entry?.image_url?.trim()) {
    return [{ imageUrl: entry.image_url.trim(), isPrimary: true }];
  }

  return [];
}

export default function DocsAppPage() {
  const { docSlug = '' } = useParams();
  const page = getDocsAppPage(docSlug);
  const [screenshots, setScreenshots] = useState<PublishedAppScreenshot[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadScreenshots = async () => {
      if (!docSlug) {
        setScreenshots([]);
        return;
      }

      const { data } = await supabase
        .from('docs_app_entries')
        .select('image_url, screenshots')
        .eq('app_id', docSlug)
        .eq('is_visible', true)
        .maybeSingle();

      if (!cancelled) {
        setScreenshots(normalizeScreenshots((data as PublishedDocsAppEntry | null) ?? null));
      }
    };

    void loadScreenshots();

    return () => {
      cancelled = true;
    };
  }, [docSlug]);

  const normalizedScreenshots = useMemo(() => screenshots, [screenshots]);

  return page ? <SemanticPageDocument page={page} screenshots={normalizedScreenshots} /> : <NotFound />;
}
